import React, { useState, useEffect, useRef } from 'react';
import { API, apiFetch } from '../api';
import { VERSION } from '../version';

// ── Device token ──────────────────────────────────────────────────────────────
// A random ID stored in localStorage — identifies this browser/device.
// Trusted for 30 days after a successful 2FA verification.

function getDeviceToken() {
  let token = localStorage.getItem('cm_device');
  if (!token) {
    token = (crypto.randomUUID
      ? crypto.randomUUID()
      : Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map(b => b.toString(16).padStart(2, '0')).join('')
    );
    localStorage.setItem('cm_device', token);
  }
  return token;
}

// ─────────────────────────────────────────────────────────────────────────────

const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #DB3267 0%, #a02050 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '24px',
  },
  card: {
    background: '#fff', borderRadius: '16px', padding: '36px 28px',
    width: '100%', maxWidth: '380px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  },
  logos: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '28px' },
  sahLogo: { height: '40px', width: 'auto' },
  divider: { width: '1px', height: '32px', background: '#e0e0e0' },
  capnetLogo: { height: '28px', width: 'auto', opacity: 0.7 },
  title: { fontSize: '22px', fontWeight: '700', color: '#222', marginBottom: '4px', textAlign: 'center' },
  subtitle: { fontSize: '13px', color: '#888', marginBottom: '28px', textAlign: 'center' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: {
    width: '100%', padding: '12px 14px', border: '1.5px solid #e0e0e0',
    borderRadius: '8px', fontSize: '15px', marginBottom: '16px', outline: 'none',
    transition: 'border-color 0.2s', boxSizing: 'border-box',
  },
  btn: {
    width: '100%', padding: '14px', background: '#DB3267', color: '#fff',
    borderRadius: '8px', fontWeight: '700', fontSize: '16px', marginTop: '4px',
    transition: 'background 0.2s', border: 'none', cursor: 'pointer',
  },
  btnDisabled: { background: '#e0e0e0', color: '#aaa', cursor: 'not-allowed' },
  btnGhost: {
    width: '100%', padding: '12px', background: 'none', color: '#DB3267',
    borderRadius: '8px', fontWeight: '600', fontSize: '14px', marginTop: '10px',
    border: '1.5px solid #f0b8cc', cursor: 'pointer',
  },
  error: {
    background: '#fce8ef', color: '#b8254f', borderRadius: '8px',
    padding: '10px 14px', fontSize: '13px', marginBottom: '16px', textAlign: 'center',
  },
  registerLink: {
    marginTop: '20px', textAlign: 'center', fontSize: '13px', color: '#888',
  },
  poweredBy: {
    marginTop: '20px', textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
  capnetSmall: { height: '16px', opacity: 0.7, filter: 'brightness(10)' },

  // OTP specific
  otpTitle: { fontSize: '20px', fontWeight: '700', color: '#222', marginBottom: '6px', textAlign: 'center' },
  otpSub: { fontSize: '13px', color: '#888', marginBottom: '24px', textAlign: 'center', lineHeight: '1.6' },
  otpInputRow: { display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '20px' },
  otpDigit: {
    width: '44px', height: '54px', textAlign: 'center', fontSize: '22px', fontWeight: '700',
    border: '2px solid #e0e0e0', borderRadius: '10px', outline: 'none',
    color: '#222', background: '#fafafa', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  otpDigitFocused: { borderColor: '#DB3267', background: '#fff' },
  resendRow: { textAlign: 'center', marginTop: '16px', fontSize: '13px', color: '#aaa' },
  resendBtn: { background: 'none', border: 'none', color: '#DB3267', fontWeight: '700', fontSize: '13px', cursor: 'pointer', padding: 0 },
  backBtn: { background: 'none', border: 'none', color: '#aaa', fontWeight: '600', fontSize: '13px', cursor: 'pointer', padding: 0 },
  shieldIcon: { fontSize: '36px', textAlign: 'center', marginBottom: '12px' },
};

// ── OTP digit input ───────────────────────────────────────────────────────────

function OtpInput({ onComplete }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [focused, setFocused] = useState(0);
  const inputs = useRef([]);

  function handleChange(i, val) {
    const cleaned = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = cleaned;
    setDigits(next);
    if (cleaned && i < 5) {
      inputs.current[i + 1]?.focus();
      setFocused(i + 1);
    }
    if (next.every(d => d !== '')) {
      onComplete(next.join(''));
    }
  }

  function handleKeyDown(i, e) {
    if (e.key === 'Backspace') {
      if (digits[i]) {
        const next = [...digits];
        next[i] = '';
        setDigits(next);
      } else if (i > 0) {
        inputs.current[i - 1]?.focus();
        setFocused(i - 1);
      }
    }
    if (e.key === 'ArrowLeft' && i > 0) { inputs.current[i - 1]?.focus(); setFocused(i - 1); }
    if (e.key === 'ArrowRight' && i < 5) { inputs.current[i + 1]?.focus(); setFocused(i + 1); }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setDigits(next);
    const lastFilled = Math.min(pasted.length, 5);
    inputs.current[lastFilled]?.focus();
    setFocused(lastFilled);
    if (pasted.length === 6) onComplete(pasted);
  }

  return (
    <div style={s.otpInputRow}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          style={{ ...s.otpDigit, ...(focused === i ? s.otpDigitFocused : {}) }}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onFocus={() => setFocused(i)}
          onPaste={handlePaste}
          autoComplete="one-time-code"
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

// ── Main Login component ──────────────────────────────────────────────────────

export default function Login({ onLogin, onRegister }) {
  const [step, setStep]           = useState('login'); // 'login' | 'otp'
  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  // OTP state
  const [userId, setUserId]       = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [resending, setResending] = useState(false);

  const deviceToken = getDeviceToken();

  // ── Step 1: username + password ───────────────────────────────────────────

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim() || loading) return;
    setError('');
    setLoading(true);
    try {
      const res  = await apiFetch(`${API}/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: username.trim().toLowerCase(), password, deviceToken }),
      });
      const data = await res.json();
      if (data.ok && data.requiresOtp) {
        setUserId(data.userId);
        setMaskedPhone(data.maskedPhone);
        setStep('otp');
      } else if (data.ok) {
        onLogin(data.user);
      } else {
        setError(data.error || 'Login failed. Please try again.');
      }
    } catch {
      setError('Cannot connect to the server. Please check your connection.');
    }
    setLoading(false);
  }

  // ── Step 2: OTP verification ──────────────────────────────────────────────

  async function handleOtp(code) {
    setError('');
    setLoading(true);
    try {
      const res  = await apiFetch(`${API}/verify-otp`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ userId, code, deviceToken }),
      });
      const data = await res.json();
      if (data.ok) {
        onLogin(data.user);
      } else {
        setError(data.error || 'Verification failed.');
        setLoading(false);
      }
    } catch {
      setError('Cannot connect to the server. Please check your connection.');
      setLoading(false);
    }
  }

  async function resendCode() {
    setResending(true);
    setError('');
    try {
      await apiFetch(`${API}/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: username.trim().toLowerCase(), password, deviceToken: null }),
      });
    } catch {}
    setResending(false);
  }

  function backToLogin() {
    setStep('login');
    setError('');
    setUserId(null);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logos}>
          <img src="/sah-logo.svg" alt="Support at Home" style={s.sahLogo} />
          <div style={s.divider} />
          <img src="/capnet-logo.jpg" alt="CapNet" style={s.capnetLogo} />
        </div>

        {step === 'login' ? (
          <>
            <h1 style={s.title}>Care Moments</h1>
            <p style={s.subtitle}>Share the moments that matter</p>

            {error && <div style={s.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <label style={s.label}>Username</label>
              <input
                style={s.input}
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="Enter your username"
              />
              <label style={s.label}>Password</label>
              <input
                style={s.input}
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
              <button
                style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
                type="submit"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={s.registerLink}>
              New staff member?{' '}
              <button
                onClick={onRegister}
                style={{ background: 'none', border: 'none', color: '#DB3267', fontWeight: '700', fontSize: '13px', cursor: 'pointer', padding: 0 }}
              >
                Request access
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={s.shieldIcon}>🔐</div>
            <h1 style={s.otpTitle}>Verification required</h1>
            <p style={s.otpSub}>
              We sent a 6-digit code to your mobile number ending in{' '}
              <strong>••••{maskedPhone}</strong>.{' '}
              Enter it below to continue.
            </p>

            {error && <div style={s.error}>{error}</div>}

            {!loading && <OtpInput key={error} onComplete={handleOtp} />}

            {loading && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#aaa', fontSize: '14px' }}>
                Verifying...
              </div>
            )}

            <button
              style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
              onClick={() => {
                const inputs = document.querySelectorAll('input[type="tel"]');
                const code = Array.from(inputs).map(i => i.value).join('');
                if (code.length === 6) handleOtp(code);
              }}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <div style={s.resendRow}>
              Didn't receive it?{' '}
              <button style={s.resendBtn} onClick={resendCode} disabled={resending}>
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>

            <div style={{ ...s.resendRow, marginTop: '10px' }}>
              <button style={s.backBtn} onClick={backToLogin}>← Back to login</button>
            </div>

            <div style={{ fontSize: '11px', color: '#ccc', textAlign: 'center', marginTop: '16px' }}>
              This device will be trusted for 30 days
            </div>
          </>
        )}
      </div>

      <div style={s.poweredBy}>
        <span>Powered by</span>
        <img src="/capnet-logo.jpg" alt="CapNet" style={s.capnetSmall} />
        <span style={{ fontSize: '10px', opacity: 0.4, marginLeft: '6px' }}>{VERSION}</span>
      </div>
    </div>
  );
}
